import React, { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@praxis/component-auth'
import { findIndex, first } from 'lodash'
import { Grid, Tabs, Typography, Box, Checkbox } from '@mui/material'
import SimpleImageCard from '../../components/ImageCard/SimpleImageCard'
import AnnotationWorkspace from './AnnotationWorkspace'
import PreviewHeader from './PreviewHeader'
import { makeStyles } from '@material-ui/core/styles'
import WorkflowTable from '../Workflow/WorkflowTable'
import { resetAdvancedPreviwData } from '../../store/Workflow/actionCreator'
import apiConfig from '../../apiConfig'

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
    marginTop: 64,
  },
  outlinedButton: {
    color: '#ffffff !important',
    border: '1px solid #ffffff !important',
    background: 'transparent !important',
  },
  button: {
    height: '32px',
    minWidth: '90px',
    marginRight: '8px !important',
    fontFamily: 'Roboto !important',
    fontSize: '14px !important',
    fontStyle: 'normal !important',
    fontWeight: '500 !important',
  },
  containedButton: {
    background: '#ffffff !important',
    border: ' 1px solid #ffffff !important',
    color: '#CC0000 !important',
    '&:hover': {
      backgroundColor: '#ffffff !important',
      color: '#CC0000 !important',
      border: ' 1px solid #ffffff !important',
    },
    '&:focus': {
      backgroundColor: '#ffffff !important',
      color: '#CC0000 !important',
      border: ' 1px solid #ffffff !important',
    },
  },
}))

export default function AdvancedPreview() {
  const auth = useAuth()
  const {
    session: {
      userInfo: { lanId = '', displayname = '', email = '' } = {},
    } = {},
  } = auth
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const workflowTableRef = React.useRef(null)
  const [data, setData] = useState([])
  const [finalSelectedData, setFinalSelectedData] = useState([])
  const [activeImage, setActiveImage] = useState({})
  const [workflowTableProps, setWorkflowTableProps] = useState({})
  const [tableTab, setTableTab] = useState('')
  const [isWorkflowVisible, setIsWorkflowVisible] = useState(false)
  const [, setPreviewData] = useState([])
  const [viewMode, setViewMode] = useState('preview')

  const channel = new BroadcastChannel('selectionChannel')

  const [advancedPreviewDataList = {}] = useSelector((state) => {
    const {
      workflowReducer: {
        advancedPreviewData: advancedPreviewDataList = {},
      } = {},
    } = state
    return [advancedPreviewDataList]
  })

  const { response_list: advancedPreviewData = [] } =
    advancedPreviewDataList || {}
  useEffect(() => {
    if (advancedPreviewData?.length) {
      setPreviewData(advancedPreviewData) //TODO - NEEDS TO VALIDATE - Maximum update depth exceeded
      const advancedPreviewFileNames = new Set(
        advancedPreviewData.map((obj) => obj.file_name)
      )
      const filteredData = data.filter((obj) => {
        return !advancedPreviewFileNames.has(obj.fileName)
      })
      setData(filteredData)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advancedPreviewData])

  useEffect(() => {
    const cleanup = () => {
      dispatch(resetAdvancedPreviwData())
      localStorage.removeItem('selectionState')
      localStorage.removeItem('workflowTableProps')
      channel.close()
    }
    dispatch(resetAdvancedPreviwData())
    window.addEventListener('beforeunload', cleanup)
    const selectionState = localStorage.getItem('selectionState')
    const selectionStateData = selectionState ? JSON.parse(selectionState) : []
    const workflowTablePropsData = localStorage.getItem('workflowTableProps')
    const workflowTabData = workflowTablePropsData
      ? JSON.parse(workflowTablePropsData)
      : {}
    const activeSelImage = first(selectionStateData || []) || {}
    if (selectionState) {
      setData(selectionStateData)
      setFinalSelectedData(selectionStateData)
      setActiveImage(activeSelImage)
      setWorkflowTableProps(workflowTabData)
      setIsWorkflowVisible(false)
      setPreviewData([])
      workflowTableRef?.current?.showWorkflowDrawer(
        workflowTabData?.tableTab || '',
        selectionStateData,
        'advancedpreviewonload',
        workflowTabData?.qaAsigneeList,
        workflowTabData?.asigneeList
      )
      setTableTab(workflowTabData?.tableTab || '')
    } else {
      navigate('/workflow')
    }
    channel.onmessage = (event) => {
      const data = event.data || {}
      const { workflowTableProps = {}, selection = {} } = data
      const workflowTabData =
        Object.entries(workflowTableProps)?.length > 0
          ? JSON.parse(workflowTableProps)
          : {}
      const selectionData =
        Object.entries(selection)?.length > 0 ? JSON.parse(selection) : {}
      setData(selectionData)
      setFinalSelectedData(selectionData)
      setActiveImage(first(selectionData || []) || {})
      setWorkflowTableProps(workflowTabData)
      setPreviewData([])
      workflowTableRef?.current?.showWorkflowDrawer(
        workflowTabData?.tableTab || '',
        selectionData,
        'advancedpreview',
        workflowTabData?.qaAsigneeList,
        workflowTabData?.asigneeList
      )
      setTableTab(workflowTabData?.tableTab || '')
    }
    return () => {
      window.removeEventListener('beforeunload', cleanup)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!activeImage?.id && (data || []).length > 0) {
      setActiveImage(first(data) || {})
    }
  }, [data, activeImage])

  useEffect(() => {
    if (!isWorkflowVisible) return
    workflowTableRef?.current?.showWorkflowDrawer(
      workflowTableProps?.tableTab,
      finalSelectedData,
      'advancedpreview',
      workflowTableProps?.qaAsigneeList,
      workflowTableProps?.asigneeList
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isWorkflowVisible, workflowTableProps]) //TODO - NEEDS TO VALIDATE - Maximum update depth exceeded

  const classes = useStyles()
  const [pageSize, setPageSize] = useState(4)
  const isAnnotationMode = viewMode === 'annotation'

  const showWorkflowFlyout = () => {
    setIsWorkflowVisible(true)
  }

  const onPreviewCheckChange = (asset) => {
    const selected = { ...asset }
    const { id = '' } = selected
    const selectedIndex = findIndex(finalSelectedData, function (obj = {}) {
      return obj.id === id
    })

    let newSelected = []
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(finalSelectedData, selected)
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(finalSelectedData.slice(1))
    } else if (selectedIndex === finalSelectedData.length - 1) {
      newSelected = newSelected.concat(finalSelectedData.slice(0, -1))
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        finalSelectedData.slice(0, selectedIndex),
        finalSelectedData.slice(selectedIndex + 1)
      )
    }
    setFinalSelectedData(newSelected)
  }
  return (
    <div>
      <PreviewHeader
        classes={classes}
        pageSize={pageSize}
        onButtonClick={(size) => {
          setIsWorkflowVisible(false)
          setPageSize(size)
          setViewMode('preview')
        }}
        showWorkflowFlyout={showWorkflowFlyout}
        viewMode={viewMode}
        onViewModeChange={(mode) => {
          setIsWorkflowVisible(false)
          setViewMode(mode)
        }}
      />

      <div style={{ marginTop: '65px' }}>
        <Grid container>
          <Grid item xs={isAnnotationMode || apiConfig?.isPol ? 12 : 11}>
            {!isAnnotationMode && (
              <Typography
                style={{
                  marginLeft: '40px',
                  color: '#000',
                  fontFamily: 'Roboto',
                  fontSize: '20px',
                  fontStyle: 'normal',
                  fontWeight: '500',
                  lineHeight: 'normal',
                  marginTop: '24px',
                }}
              >
                Showing {data?.length || 0} images
              </Typography>
            )}
            {isAnnotationMode ? (
              <AnnotationWorkspace
                data={data}
                activeImage={activeImage}
                setActiveImage={setActiveImage}
                finalSelectedData={finalSelectedData}
                onPreviewCheckChange={onPreviewCheckChange}
                displayname={displayname}
                lanId={lanId}
                email={email}
              />
            ) : pageSize === 12 ? (
              <>
                <Box display="flex" justifyContent="center" width="100%">
                  <Tabs
                    style={{ marginTop: '10px' }}
                    centered
                    variant="scrollable"
                    value={false}
                    scrollButtons="auto"
                    aria-label="scrollable auto tabs example"
                  >
                    {data?.map((dat, i) => {
                      const isCardChecked =
                        (finalSelectedData || [])?.findIndex(
                          (obj) => obj?.id === dat?.id
                        ) !== -1
                      return (
                        <div
                          key={dat?.id || i}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '8px 10px',
                            marginRight: '12px',
                            background: '#fff',
                            border: `2px solid ${
                              activeImage?.id === dat?.id ? '#CC0000' : '#000'
                            }`,
                            cursor: 'pointer',
                          }}
                          onClick={() => {
                            setActiveImage(dat)
                          }}
                        >
                          {!apiConfig?.isPol && (
                            <Checkbox
                              checked={isCardChecked}
                              onChange={(event) => {
                                onPreviewCheckChange(dat)
                              }}
                              id={dat?.id || ''}
                              inputProps={{ 'aria-label': 'controlled' }}
                            />
                          )}
                          <img
                            alt={dat.id}
                            style={{
                              height: '90px',
                              width: '90px',
                              objectFit: 'contain',
                              background: '#fff',
                            }}
                            src={dat.previewImage}
                          />
                        </div>
                      )
                    })}
                  </Tabs>
                </Box>
                <Grid
                  item
                  lg={12}
                  sm={12}
                  md={12}
                  style={{
                    height: '900px',
                    marginTop: '24px',
                    paddingTop: '10px',
                    borderTop: '1px solid #000',
                  }}
                >
                  <div
                    style={{ width: '80%', height: '100%', margin: '0 auto' }}
                  >
                    <SimpleImageCard
                      title={`${activeImage.id}, ${activeImage.version}`}
                      imageSrc={activeImage.zoomImage}
                      zoomSrc={activeImage.zoomImage}
                    />
                  </div>
                </Grid>
              </>
            ) : (
              <Grid
                wrap
                style={{ width: '100%', padding: '20px 40px' }}
                spacing={2}
                container
                className={classes.root}
              >
                {data?.map((dat, i) => {
                  const isCardChecked =
                    (finalSelectedData || [])?.findIndex(
                      (obj) => obj?.id === dat?.id
                    ) !== -1
                  return (
                    <Grid
                      key={dat?.id || ''}
                      container
                      item
                      lg={pageSize}
                      justifyContent={'flex-start'}
                      alignItems={'center'}
                    >
                      {!apiConfig?.isPol && (
                        <Checkbox
                          checked={isCardChecked}
                          onChange={(event) => {
                            onPreviewCheckChange(dat)
                          }}
                          id={dat?.id || ''}
                          inputProps={{ 'aria-label': 'controlled' }}
                        />
                      )}
                      <SimpleImageCard
                        title={`${dat.id}, ${dat.version}`}
                        imageSrc={dat.zoomImage}
                        zoomSrc={dat.zoomImage}
                      />
                    </Grid>
                  )
                })}
              </Grid>
            )}
          </Grid>
          {!apiConfig?.isPol &&
            isWorkflowVisible &&
            (isAnnotationMode ? (
              <div style={{ display: 'none' }}>
                <WorkflowTable ref={workflowTableRef} tableTab={tableTab} />
              </div>
            ) : (
              <Grid item xs={1}>
                <WorkflowTable ref={workflowTableRef} tableTab={tableTab} />
              </Grid>
            ))}
        </Grid>
      </div>
    </div>
  )
}
